import { Temporal } from '@js-temporal/polyfill'

import type { CalcValue, FormulaEvaluationContext } from './types'
import { isCalendarDate } from '../utils/calendar-date'

export type FormulaDateUnit =
  | 'days'
  | 'weeks'
  | 'months'
  | 'years'
  | 'hours'
  | 'minutes'
type FormulaDate = Temporal.PlainDate | Temporal.Instant

const checkedZones = new Set(['UTC'])
export function validateFormulaZone(timeZone: string): void {
  if (!timeZone || /^[+-]/.test(timeZone))
    throw new RangeError('A named formula timezone is required')
  if (checkedZones.has(timeZone)) return
  new Intl.DateTimeFormat('en-US', { timeZone })
  if (checkedZones.size >= 64) checkedZones.clear()
  checkedZones.add(timeZone)
}

export function isFormulaDateValue(value: CalcValue): boolean {
  if (value instanceof Date) return true
  // Ordinary status/name comparisons must not allocate parsing exceptions.
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}(?:T|$)/i.test(value))
    return false
  try {
    return readDate(value) !== null
  } catch {
    return false
  }
}

/** Calendar strings retain their day; timestamps must have an explicit offset. */
function readDate(value: CalcValue): FormulaDate | null {
  if (value === null || value === '') return null
  if (typeof value === 'string') {
    const calendarValue: unknown = value
    if (isCalendarDate(calendarValue))
      return Temporal.PlainDate.from(calendarValue)
    if (
      !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})$/i.test(
        value,
      ) ||
      !isCalendarDate(value.slice(0, 10))
    )
      throw new RangeError('Use a calendar date or a timestamp with an offset')
    return Temporal.Instant.from(value)
  }
  if (value instanceof Date || typeof value === 'number')
    return Temporal.Instant.fromEpochMilliseconds(
      value instanceof Date ? value.getTime() : value,
    )
  throw new RangeError('Expected a date')
}

function zone(context: FormulaEvaluationContext): string {
  if (!context.timeZone || /^[+-]/.test(context.timeZone))
    throw new RangeError('A named formula timezone is required')
  return context.timeZone
}

function instant(
  value: FormulaDate,
  context: FormulaEvaluationContext,
): Temporal.Instant {
  return value instanceof Temporal.PlainDate
    ? value.toZonedDateTime(zone(context)).toInstant()
    : value
}

function calendar(
  value: FormulaDate,
  context: FormulaEvaluationContext,
): Temporal.PlainDate {
  return value instanceof Temporal.PlainDate
    ? value
    : value.toZonedDateTimeISO(zone(context)).toPlainDate()
}

export function formulaDateInstant(
  value: CalcValue,
  context: FormulaEvaluationContext,
): Date | null {
  const date = readDate(value)
  return date === null
    ? null
    : new Date(instant(date, context).epochMilliseconds)
}

export function formulaToday(context: FormulaEvaluationContext): string {
  return Temporal.Instant.fromEpochMilliseconds(context.now!.getTime())
    .toZonedDateTimeISO(zone(context))
    .toPlainDate()
    .toString()
}

export function formulaDatePart(
  value: CalcValue,
  part: 'year' | 'month' | 'day',
  context: FormulaEvaluationContext,
): number | null {
  const date = readDate(value)
  return date === null ? null : calendar(date, context)[part]
}

/** Days/weeks count calendar boundaries; hours/minutes measure elapsed time. */
export function formulaDateDifference(
  fromValue: CalcValue,
  toValue: CalcValue,
  unit: FormulaDateUnit,
  context: FormulaEvaluationContext,
): number | null {
  const from = readDate(fromValue)
  const to = readDate(toValue)
  if (from === null || to === null) return null
  if (unit === 'hours' || unit === 'minutes')
    return Math.trunc(
      (instant(to, context).epochMilliseconds -
        instant(from, context).epochMilliseconds) /
        (unit === 'hours' ? 3_600_000 : 60_000),
    )
  const first = calendar(from, context)
  const last = calendar(to, context)
  if (unit === 'years') return last.year - first.year
  if (unit === 'months')
    return (last.year - first.year) * 12 + last.month - first.month
  const days = first.until(last, { largestUnit: 'days' }).days
  return unit === 'weeks' ? Math.trunc(days / 7) : days
}

/** Calendar units preserve wall time and clamp month ends; hours/minutes are elapsed. */
export function formulaDateAdd(
  value: CalcValue,
  amount: number,
  unit: FormulaDateUnit,
  context: FormulaEvaluationContext,
): CalcValue {
  const date = readDate(value)
  if (date === null) return null
  if (!Number.isFinite(amount)) throw new RangeError('Invalid date offset')
  if (unit === 'hours' || unit === 'minutes') {
    const milliseconds = Math.trunc(
      amount * (unit === 'hours' ? 3_600_000 : 60_000),
    )
    return new Date(
      instant(date, context).add({ milliseconds }).epochMilliseconds,
    )
  }
  if (!Number.isSafeInteger(amount))
    throw new RangeError('Calendar offsets must be whole numbers')
  if (date instanceof Temporal.PlainDate) {
    const result = date
      .add({ [unit]: amount }, { overflow: 'constrain' })
      .toString()
    if (!isCalendarDate(result))
      throw new RangeError('Calendar date out of range')
    return result
  }
  return new Date(
    date
      .toZonedDateTimeISO(zone(context))
      .add({ [unit]: amount }, { overflow: 'constrain' }).epochMilliseconds,
  )
}
